import { useParams, Link, useNavigate } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import ContentCard from "@/components/feature/ContentCard";
import EmptyState from "@/components/feature/EmptyState";
import { getContentById, getCategoryById, getRelatedContents, formatViews } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";
import ShareTools from "./components/ShareTools";

export default function DetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const item = getContentById(id);
  const category = item ? getCategoryById(item.categoryId) : undefined;
  const related = item ? getRelatedContents(item, 10) : [];

  usePageMeta(
    `${item?.title ?? "作品详情"} - 国语对白做受69`,
    item ? item.description.join("").slice(0, 150) : "国语对白做受69作品详情页",
    item ? `${item.title},${item.genres.join(",")},${item.tags.join(",")},国语对白做受69` : "国语对白做受69",
  );

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteNavbar />
        <main className="flex-1 w-full mx-auto max-w-page px-4 md:px-8 py-16">
          <EmptyState
            title="没有找到该作品"
            description="你访问的作品可能已被移除或链接有误，返回首页继续浏览其他国语影音资源。"
            actionLabel="返回首页"
            onAction={() => navigate("/")}
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const metaRows = [
    { label: "上映年份", value: String(item.year) },
    { label: "地区", value: item.region },
    { label: "类型", value: item.genres.join(" / ") },
    { label: "导演 / 主创", value: item.director },
    { label: "主要演员", value: item.cast.join("、") },
    { label: "最近更新", value: item.updatedAt },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteNavbar />

      <main className="flex-1 w-full">
        <section className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={item.cover}
              alt=""
              className="w-full h-full object-cover object-top blur-xl scale-110"
            />
            <div className="absolute inset-0 bg-primary-950/85" />
          </div>

          <div className="relative mx-auto max-w-page px-4 md:px-8 py-8 md:py-12">
            <div className="text-background-300">
              <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category?.name ?? "分类", to: `/category/${item.categoryId}` }, { label: item.title }]} />
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-6 md:gap-8">
              <div className="w-40 sm:w-48 md:w-56 shrink-0 mx-auto sm:mx-0">
                <div className="aspect-[2/3] rounded-lg overflow-hidden ring-1 ring-background-50/20">
                  <img
                    src={item.cover}
                    alt={`${item.title} 作品封面`}
                    title={`${item.title} 国语对白高清影音`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/category/${item.categoryId}`}
                    className="rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50 cursor-pointer hover:bg-accent-600 transition-colors"
                  >
                    {category?.name}
                  </Link>
                  <span className="rounded-full border border-background-50/30 px-3 py-1 text-xs text-background-200">
                    {item.region}
                  </span>
                  <span className="rounded-full border border-background-50/30 px-3 py-1 text-xs text-background-200">
                    {item.year}
                  </span>
                </div>

                <h1 className="mt-3.5 text-2xl md:text-4xl font-bold text-background-50 font-heading">
                  {item.title}
                </h1>

                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-200">
                  <span className="flex items-center gap-1 text-accent-300 font-medium">
                    <i className="ri-star-fill" />
                    {item.rating} 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" />
                    {formatViews(item.views)} 次浏览
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-mic-line" />
                    国语对白
                  </span>
                </div>

                <p className="mt-4 max-w-2xl text-sm md:text-[15px] text-background-200 leading-relaxed">
                  {item.summary}
                </p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="rounded-full bg-background-50/10 border border-background-50/25 px-3 py-1 text-xs text-background-100 hover:bg-background-50/20 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })}
                    className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-file-text-line" />
                    查看详细内容
                  </button>
                  <Link
                    to={`/category/${item.categoryId}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-50/30 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-grid-line" />
                    进入分类
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-page px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-2">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                内容简介
              </h2>
              <article className="mt-4 space-y-4">
                {item.description.map((para, i) => (
                  <p key={i} className="text-sm md:text-[15px] text-foreground-700 leading-loose">
                    {para}
                  </p>
                ))}
              </article>

              <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <i className="ri-information-line text-accent-500 text-base" />
                  <span className="font-medium text-foreground-800">演示范围说明</span>
                </div>
                <p className="mt-2 text-xs md:text-[13px] text-foreground-500 leading-relaxed">
                  本站为内容展示与介绍平台，页面仅提供作品资料、标签导航与关联推荐，不提供在线播放、下载、会员及支付服务。
                </p>
              </div>

              <div className="mt-8">
                <h3 className="text-base font-semibold text-foreground-900 font-heading mb-3">相关标签</h3>
                <div className="flex flex-wrap gap-2">
                  {item.genres.map((g) => (
                    <Link
                      key={g}
                      to={`/search?q=${encodeURIComponent(g)}`}
                      className="rounded-full bg-primary-50 border border-primary-200 px-3.5 py-1.5 text-xs text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer"
                    >
                      {g}
                    </Link>
                  ))}
                  {item.tags.map((t) => (
                    <Link
                      key={t}
                      to={`/search?q=${encodeURIComponent(t)}`}
                      className="rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs text-secondary-900 hover:bg-accent-100 hover:text-accent-900 transition-colors cursor-pointer"
                    >
                      {t}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 lg:sticky lg:top-24">
                <h2 className="text-base font-semibold text-foreground-900 font-heading mb-4 flex items-center gap-2">
                  <i className="ri-list-check-2 text-accent-500" />
                  作品信息
                </h2>
                <dl className="space-y-3">
                  {metaRows.map((row) => (
                    <div key={row.label} className="flex gap-3 text-sm">
                      <dt className="w-20 shrink-0 text-foreground-500">{row.label}</dt>
                      <dd className="flex-1 text-foreground-800">{row.value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-5 pt-5 border-t border-background-200">
                  <p className="text-xs text-foreground-500 mb-2">所属分类</p>
                  <Link
                    to={`/category/${item.categoryId}`}
                    className="flex items-center gap-2 rounded-md bg-background-100 px-3.5 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <i className={`${category?.icon ?? "ri-film-line"} text-accent-500 text-base`} />
                    {category?.name}
                    <i className="ri-arrow-right-s-line ml-auto" />
                  </Link>
                </div>

                <div className="mt-5 pt-5 border-t border-background-200">
                  <ShareTools title={item.title} />
                </div>
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <section className="mt-12 md:mt-16">
              <SectionHeading title="相关推荐" subtitle="根据类型与标签为你推荐同类国语影音作品" />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {related.slice(0, 10).map((rel, i) => (
                  <ContentCard key={rel.id} item={rel} index={i} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}